import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { UserProfile, UserRole } from '../lib/database.types';

type EffectiveRole = UserRole | null;

type AuthState = {
  session: Session | null;
  user: User | null;
  profile: UserProfile | null;
  role: EffectiveRole;
  loading: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
};

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isVendor, setIsVendor] = useState(false);
  const [loading, setLoading] = useState(true);

  async function applyPendingSignup(userId: string) {
    // If the user went through /signup and the profile is missing first/last/team,
    // patch from the values they entered (stashed in sessionStorage).
    const raw = sessionStorage.getItem('ga_pending_signup');
    if (!raw) return;
    try {
      const pending = JSON.parse(raw) as { first_name?: string; last_name?: string; team?: string };
      if (!pending.first_name && !pending.last_name && !pending.team) {
        sessionStorage.removeItem('ga_pending_signup');
        return;
      }
      const update: Record<string, string | null> = {};
      if (pending.first_name) update.first_name = pending.first_name;
      if (pending.last_name) update.last_name = pending.last_name;
      if (pending.team) update.team = pending.team;
      if (pending.first_name || pending.last_name) {
        update.full_name = `${pending.first_name ?? ''} ${pending.last_name ?? ''}`.trim();
      }
      await supabase.from('ga_user_profiles').update(update).eq('id', userId);
      sessionStorage.removeItem('ga_pending_signup');
    } catch (e) {
      console.error('[AuthContext] applyPendingSignup failed', e);
    }
  }

  async function loadProfileAndRole(userId: string) {
    try {
      await applyPendingSignup(userId);
      const [profileRes, vendorRes] = await Promise.all([
        supabase.from('ga_user_profiles').select('*').eq('id', userId).maybeSingle(),
        supabase.from('ga_vendor_members').select('vendor_id').eq('user_id', userId).limit(1),
      ]);
      setProfile((profileRes.data as UserProfile | null) ?? null);
      setIsVendor(!!vendorRes.data && (vendorRes.data as unknown[]).length > 0);
    } catch (e) {
      console.error('[AuthContext] loadProfileAndRole failed', e);
    }
  }

  useEffect(() => {
    // Subscribe first so we catch INITIAL_SESSION + every subsequent change in one place.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setLoading(false);
      if (newSession?.user) {
        loadProfileAndRole(newSession.user.id);
      } else {
        setProfile(null);
        setIsVendor(false);
      }
    });

    // Also fire an explicit getSession so we don't depend on the listener firing
    // before the first paint. Either path is sufficient to flip loading=false.
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
      if (data.session?.user) loadProfileAndRole(data.session.user.id);
    }).catch((e) => {
      console.error('[AuthContext] getSession failed', e);
      setLoading(false);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const role: EffectiveRole = useMemo(() => {
    if (!session?.user) return null;
    if (profile?.role === 'platform') return 'platform';
    if (isVendor) return 'vendor';
    return 'member';
  }, [session, profile, isVendor]);

  const value = useMemo<AuthState>(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      role,
      loading,
      signOut: async () => {
        await supabase.auth.signOut();
      },
      refresh: async () => {
        if (session?.user) await loadProfileAndRole(session.user.id);
      },
    }),
    [session, profile, role, loading],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
